import { Checkbox, Group, NumberInput, Stack, Switch, Text, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { LOCKOUT_N_MAX, LOCKOUT_N_MIN, REUSE_N_MAX, REUSE_N_MIN } from '@mygpt/app/apis/services/security';
import type { PasswordRules } from '@mygpt/app/apis/services/auth';

interface PasswordRulesFieldsProps {
  value: PasswordRules;
  onChange: (value: PasswordRules) => void;
  /** 继承态灰显只读 */
  disabled?: boolean;
  /** 生效的 MFA 是否开启（用于「无期限」开关联动） */
  mfaEnabled: boolean;
}

/**
 * 密码规则表单字段（超管「安全设置」与企业「安全策略」共用）
 * 6 个勾选项 + reuse_n / lockout_n 数字输入 + 无期限开关（§0.3）
 */
export const PasswordRulesFields = ({ value, onChange, disabled = false, mfaEnabled }: PasswordRulesFieldsProps) => {
  const { t } = useTranslation();

  const update = (patch: Partial<PasswordRules>) => {
    const next = { ...value, ...patch };
    // 前端联动：取消「90 天内更新密码」时同时关闭「无期限」（契约 NO_EXPIRY_PREREQUISITE 前置）
    if (patch.rotate_90d === false) {
      next.no_expiry = false;
    }
    onChange(next);
  };

  // 无期限开关前置：生效 MFA 开启 且 勾选 90 天内更新密码
  const noExpiryAllowed = mfaEnabled && value.rotate_90d;

  const numberInput = (key: 'reuse_n' | 'lockout_n', enabled: boolean, min: number, max: number) => (
    <NumberInput
      value={value[key]}
      onChange={(v) => {
        if (typeof v === 'number') update({ [key]: v } as Partial<PasswordRules>);
      }}
      min={min}
      max={max}
      step={1}
      clampBehavior="strict"
      allowDecimal={false}
      size="xs"
      w={70}
      disabled={disabled || !enabled}
    />
  );

  return (
    <Stack gap="sm">
      <Checkbox
        label={t('security.rules.require_case_digit')}
        checked={value.require_case_digit}
        onChange={(e) => update({ require_case_digit: e.currentTarget.checked })}
        disabled={disabled}
      />
      <Checkbox
        label={t('security.rules.require_special')}
        checked={value.require_special}
        onChange={(e) => update({ require_special: e.currentTarget.checked })}
        disabled={disabled}
      />
      <Checkbox
        label={t('security.rules.min_length_8')}
        checked={value.min_length_8}
        onChange={(e) => update({ min_length_8: e.currentTarget.checked })}
        disabled={disabled}
      />
      <Checkbox
        label={t('security.rules.rotate_90d')}
        checked={value.rotate_90d}
        onChange={(e) => update({ rotate_90d: e.currentTarget.checked })}
        disabled={disabled}
      />

      <Group gap="xs" wrap="nowrap" align="center">
        <Checkbox
          checked={value.reuse_forbidden}
          onChange={(e) => update({ reuse_forbidden: e.currentTarget.checked })}
          disabled={disabled}
          label={
            <Group gap={6} wrap="nowrap" align="center" component="span">
              <Text size="sm" component="span">
                {t('security.rules.reuse_before')}
              </Text>
            </Group>
          }
        />
        {numberInput('reuse_n', value.reuse_forbidden, REUSE_N_MIN, REUSE_N_MAX)}
        <Text size="sm" c={disabled ? 'dimmed' : undefined}>
          {t('security.rules.reuse_after')}
        </Text>
      </Group>

      <Group gap="xs" wrap="nowrap" align="center">
        <Checkbox
          checked={value.lockout_enabled}
          onChange={(e) => update({ lockout_enabled: e.currentTarget.checked })}
          disabled={disabled}
          label={
            <Text size="sm" component="span">
              {t('security.rules.lockout_before')}
            </Text>
          }
        />
        {numberInput('lockout_n', value.lockout_enabled, LOCKOUT_N_MIN, LOCKOUT_N_MAX)}
        <Text size="sm" c={disabled ? 'dimmed' : undefined}>
          {t('security.rules.lockout_after')}
        </Text>
      </Group>

      <Stack gap={6} mt="xs">
        <Text size="xs" c="dimmed">
          {t('security.rules.no_expiry_note')}
        </Text>
        <Tooltip label={t('security.rules.no_expiry_hint')} disabled={disabled || noExpiryAllowed} withArrow>
          <div style={{ width: 'fit-content' }}>
            <Switch
              label={t('security.rules.no_expiry_label')}
              checked={value.no_expiry}
              onChange={(e) => update({ no_expiry: e.currentTarget.checked })}
              disabled={disabled || !noExpiryAllowed}
            />
          </div>
        </Tooltip>
        {!disabled && !noExpiryAllowed && (
          <Text size="xs" c="dimmed">
            {t('security.rules.no_expiry_hint')}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};
